/**
 * The back-testing gate on one promoted template: which scenario type it answers and whether that type's
 * latest bulk run clears it. A linked template can auto-send only while its type is ready; an unlinked
 * one is outside the gate. Linking is manual, here, so the operator decides what a template is tested by.
 */

import { useState } from 'react';
import { Link2, Link2Off } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBackTestingTypes } from '@/hooks/use-back-testing-types';
import { useSetTemplateTypeLink } from '@/hooks/use-promoted-blueprints';
import { useAllSOPs } from '@/hooks/use-sops';
import { getApiErrorMessage } from '@/lib/utils';
import { readinessDetail } from '@/modules/back-testing/readiness/format';
import { ReadinessBadge } from '@/modules/back-testing/readiness/readiness-badge';
import type { TemplateReadiness } from '@/modules/back-testing/readiness/types';

/** Picks the protocol, then the scenario type of it, that this template is tested by. */
const TypeLinkForm = ({ blueprintId, index }: { blueprintId: string; index: number }) => {
  const [sopId, setSopId] = useState('');
  const [typeId, setTypeId] = useState('');
  const { data: sops } = useAllSOPs();
  const { data: sopTypes } = useBackTestingTypes(sopId);
  const linkMutation = useSetTemplateTypeLink();

  const link = async () => {
    try {
      await linkMutation.mutateAsync({ id: blueprintId, index, link: { sopId, typeId } });
      toast.success('Linked. The template stays on draft until back-testing clears it.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not link this template'));
    }
  };

  return (
    <div className='flex flex-col gap-2'>
      <Select
        value={sopId}
        onValueChange={(value) => {
          setSopId(value);
          setTypeId('');
        }}
      >
        <SelectTrigger className='h-8 w-full text-xs'>
          <SelectValue placeholder='Protocol' />
        </SelectTrigger>
        <SelectContent>
          {(sops ?? []).map((sop) => (
            <SelectItem key={sop.id} value={sop.id} className='text-xs'>
              {sop.intent}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={typeId} onValueChange={setTypeId} disabled={!sopId || !sopTypes?.types.length}>
        <SelectTrigger className='h-8 w-full text-xs'>
          <SelectValue placeholder={sopId && sopTypes && !sopTypes.types.length ? 'No scenario types' : 'Scenario type'} />
        </SelectTrigger>
        <SelectContent>
          {(sopTypes?.types ?? []).map((type) => (
            <SelectItem key={type.id} value={type.id} className='text-xs'>
              {type.description}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button size='sm' className='h-7 self-start text-xs' disabled={!sopId || !typeId || linkMutation.isPending} onClick={link}>
        <Link2 className='size-3.5' />
        Link
      </Button>
    </div>
  );
};

interface TemplateGateProps {
  blueprintId: string;
  template: TemplateReadiness;
  canEdit: boolean;
}

export const TemplateGate = ({ blueprintId, template, canEdit }: TemplateGateProps) => {
  const [linking, setLinking] = useState(false);
  const { link, readiness, index } = template;
  const { data: sopTypes } = useBackTestingTypes(link?.sopId ?? '');
  const unlinkMutation = useSetTemplateTypeLink();
  const typeDescription = link ? sopTypes?.types.find((type) => type.id === link.typeId)?.description : undefined;

  const unlink = async () => {
    try {
      await unlinkMutation.mutateAsync({ id: blueprintId, index, link: null });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not unlink this template'));
    }
  };

  return (
    <div className='border-b bg-muted/20 px-3 py-2' data-testid={`template-gate-${index}`}>
      <p className='mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground'>Back-testing</p>
      {link ? (
        <div className='flex flex-col gap-1.5'>
          {typeDescription && <p className='text-[12px] leading-snug text-foreground'>{typeDescription}</p>}
          <div className='flex items-start gap-2'>
            {readiness && <ReadinessBadge readiness={readiness} />}
            <p className='text-[11px] leading-snug text-muted-foreground'>
              {readiness ? readinessDetail(readiness) : 'The linked scenario type no longer exists.'}
            </p>
          </div>
          {canEdit && (
            <Button variant='ghost' size='sm' className='h-6 self-start px-1.5 text-[11px]' onClick={unlink} disabled={unlinkMutation.isPending}>
              <Link2Off className='size-3' />
              Unlink
            </Button>
          )}
        </div>
      ) : linking ? (
        <TypeLinkForm blueprintId={blueprintId} index={index} />
      ) : (
        <div className='flex items-center justify-between gap-2'>
          <p className='text-[11px] text-muted-foreground'>Not tested: this template is outside the gate.</p>
          {canEdit && (
            <Button variant='outline' size='sm' className='h-6 text-[11px]' onClick={() => setLinking(true)}>
              <Link2 className='size-3' />
              Link a type
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
